import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { AddChildAccountRegToShoppingCartRequestForCael, AddChildAccountRegToShoppingCartRequestForCelpip, ChildUserAccountForRegDto } from 'src/app/core/dtos/dto';
import { BrandEnum, TestPurposeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { RegData } from 'src/app/core/dtos/reg-data';
import { Step } from 'src/app/core/dtos/step';
import { BusinessAccountService } from 'src/app/core/services/business-account.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { DateTimeHelper } from 'src/app/core/services/helpers/date-time.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { PageStepEnum, StepProgressService } from 'src/app/core/services/step-progress.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectChildTermName } from 'src/app/state/my-account.selectors';
import { selectRegData } from 'src/app/state/reg-data.selectors';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';


@Component({
  selector: 'app-batch-registration-select-accounts',
  templateUrl: './batch-registration-select-accounts.component.html',
  styleUrls: ['./batch-registration-select-accounts.component.scss']
})
export class BatchRegistrationSelectAccountsComponent implements OnInit, OnDestroy {

  @ViewChild('successAddModal') successAddModal: ModalComponent;
  @ViewChild('confirmRemoveModal') confirmRemoveModal: ModalComponent;

  childTermName$: Observable<string>;
  selectAll = true;
  selectedChildId: number;
  successfulRegCount: number;
  brandId: BrandEnum;
  regData: RegData;
  childAccountReg: AddChildAccountRegToShoppingCartRequestForCael | AddChildAccountRegToShoppingCartRequestForCelpip;
  selectedChildsArray: ChildUserAccountForRegDto[] = [];
  filteredChildAccounts: ChildUserAccountForRegDto[];
  childAccounts: ChildUserAccountForRegDto[];
  steps: Step[];
  childAccountsForRegSubscription: Subscription;
  removeChildAccountSubscription: Subscription;
  addChildAccountRegSubscription: Subscription;
  regDataSubscription: Subscription;
  searchText: string;
  childAccountToRemove: ChildUserAccountForRegDto;
  caelTestPurposeId = TestPurposeEnum.UniversityAdmissions;

  constructor(
    private store: Store,
    private businessAccountService: BusinessAccountService,
    private loadingService: LoadingService,
    private registrationService: RegistrationService,
    private shoppingCartService: ShoppingCartService,
    private configHelper: ConfigHelper,
    private dateTimeHelper: DateTimeHelper,
    private toastService: HotToastService,
    private router: Router,
    private stepProgressService: StepProgressService
  ) {
  }

  ngOnInit() {
    this.childTermName$ = this.store.pipe(select(selectChildTermName));
    this.brandId = this.configHelper.getBrandId();
    this.steps = this.stepProgressService.getSteps(PageStepEnum.ClientAccountsSelection);
    this.regDataSubscription = this.store.pipe(select(selectRegData)).subscribe(
      result => {
        this.regData = result;
        this.getChildAccounts();
      }
    );
  }


  public get BrandEnum() {
    return BrandEnum;
  }

  getChildAccounts() {
    this.childAccountsForRegSubscription = this.businessAccountService.getChildAccountsForReg(this.regData?.testSession?.id, this.regData?.testSession?.testTypeId)
      .subscribe(
        result => {
          this.childAccounts = result.content;
          this.filteredChildAccounts = result.content;
        },
        () => {
          this.toastService.warning("Unable to load client accounts");
        }
      );
  }

  onSelect(event) {
    const selectedChildId = Number(event.target.value);
    if (event.target.checked) {
      this.filteredChildAccounts.find(x => x.userAccountId === selectedChildId).selected = true;
    } else {
      this.filteredChildAccounts.find(x => x.userAccountId === selectedChildId).selected = false;
    }
    this.updateSelectedChildsArray();
  }

  batchAddChildRegs() {
    this.selectedChildsArray = this.filteredChildAccounts.filter(x => x.selected === true && !x.regId);
    if (this.selectedChildsArray.length > 0) {
      this.successfulRegCount = 0;
      this.submitBatchReg();
    }
  }

  submitBatchReg() {
    this.loadingService.show();
    const childAccount = this.selectedChildsArray[0];
    childAccount.registering = true;

    if (this.brandId === BrandEnum.CAEL) {
      this.childAccountReg = {
        brandId: this.brandId,
        testTypeId: this.regData.testSession.testTypeId,
        testSessionId: this.regData.testSession.id,
        testPurposeId: this.caelTestPurposeId,
        childUserAccountId: childAccount.userAccountId,
        regTimeZoneUsed: this.dateTimeHelper.getLocalTimeZone()
      };
    }

    if (this.brandId === BrandEnum.CELPIP) {
      this.childAccountReg = {
        brandId: this.brandId,
        testTypeId: this.regData.testSession.testTypeId,
        testSessionId: this.regData.testSession.id,
        testPurposeId: this.regData.selectedTestPurposeOption.testPurposeId,
        childUserAccountId: childAccount.userAccountId,
        isExpressScoring: false
      };
    }
    this.addChildAccountRegSubscription = this.registrationService.addChildAccountRegToShoppingCart(this.childAccountReg)
      .subscribe(
        result => {
          this.successfulRegCount++;
          childAccount.registering = false;
          childAccount.regId = result.content.regId;
          childAccount.shoppingCartItemId = result.content.shoppingCartItemId;
          childAccount.regStatus = result.content.regStatusId;
          this.updateShoppingCart();
          childAccount.canRegister = false;
          childAccount.canRemoveFromCart = true;
          const childAccountIndex = this.selectedChildsArray.findIndex(x => x.userAccountId === childAccount.userAccountId);
          if (childAccountIndex > -1) {
            this.selectedChildsArray.splice(childAccountIndex, 1);
            if (this.selectedChildsArray.length > 0) {
              this.loadingService.hide();
              this.submitBatchReg();
            } else {
              this.selectAll = true;
              this.loadingService.hide();
              this.successAddModal.open();
            }
          }
        }
        ,
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          childAccount.registering = false;
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning("Unknown error when adding client account registration(s) to shopping cart.");
            }
          }
        }
      );
  }

  //update shoppingCart
  updateShoppingCart() {
    this.shoppingCartService.getShoppingCart()
      .subscribe(
        result => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: result.content }));
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            this.toastService.warning("Unable to load shopping cart");
          }
        });
  }

  redirectToShoppingCart() {
    this.router.navigate(['shopping-cart']);
  }

  toggleSelectAll() {
    this.filteredChildAccounts.forEach(x => x.selected = this.selectAll);
    this.updateSelectedChildsArray();
    this.selectAll = !this.selectAll;
  }

  updateSelectedChildsArray() {
    this.selectedChildsArray = this.filteredChildAccounts.filter(x => x.selected === true && !x.regId && x.canRegister);
  }

  removeFromCart(ChildAccountToRemove: ChildUserAccountForRegDto) {
    this.childAccountToRemove = ChildAccountToRemove;
    this.confirmRemoveModal.open();
  }

  confirmRemove() {
    this.removeChildAccountSubscription = this.registrationService.removeChildAccountRegFromShoppingCart(this.childAccountToRemove.regId)
      .subscribe(
        () => {
          this.childAccountToRemove.registering = false;
          this.childAccountToRemove.regId = null;
          this.childAccountToRemove.canRegister = true;
          this.childAccountToRemove.canRemoveFromCart = false;
          this.childAccountToRemove.shoppingCartItemId = null;
          this.updateShoppingCart();
        }
        ,
        error => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Unknown error when removing client account registration from shopping cart.");
          }
        }
      );
  }

  searchTextChanged() {
    if (this.searchText === null || this.searchText === 'undefined' || this.searchText === '') {
      this.filteredChildAccounts = this.childAccounts;
    }
    else {
      const childParitialName = this.searchText.toLowerCase();
      this.filteredChildAccounts = this.filteredChildAccounts.filter(x => x.fullName.toLowerCase().includes(childParitialName));
    }
  }

  ngOnDestroy() {
    this.regDataSubscription?.unsubscribe();
    this.childAccountsForRegSubscription?.unsubscribe();
    this.addChildAccountRegSubscription?.unsubscribe();
    this.removeChildAccountSubscription?.unsubscribe();

  }

}


